/**
 * TUVI - Mis compromisos
 * Bandeja transversal: todo lo que el usuario tiene pendiente en la bitácora de
 * cualquier proyecto, sin tener que entrar proyecto por proyecto.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './Detalle/BitacoraProyecto.css';
import './MisCompromisos.css';

const fmtFecha = (f) => f ? new Date(f + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Sin fecha';

const textoDias = (d) => {
  if (d == null) return '';
  if (d < 0) return `vencido hace ${Math.abs(d)} día${Math.abs(d) === 1 ? '' : 's'}`;
  if (d === 0) return 'vence hoy';
  if (d === 1) return 'vence mañana';
  return `vence en ${d} días`;
};

const MisCompromisos = ({ onAbrirProyecto }) => {
  const { success, error } = useNotification();
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [verCerrados, setVerCerrados] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const d = await client.query(`query($c: Boolean) { misCompromisos(incluir_cerrados: $c) {
        id descripcion fecha_entrega prioridad estado semaforo dias_para_vencer
        proyecto_id proyecto_nombre apartamento creado_por_nombre origen
      } }`, { c: verCerrados });
      setItems(d.misCompromisos || []);
    } catch { error('No se pudieron cargar tus compromisos'); }
    finally { setCargando(false); }
  }, [verCerrados, error]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (c, estado) => {
    let nota = null;
    if (estado === 'cumplido' || estado === 'cancelado') {
      nota = window.prompt(estado === 'cumplido' ? '¿Cómo se resolvió?' : '¿Por qué se cancela?');
      if (nota === null) return;
      if (!nota.trim()) { error('Debes escribir la nota'); return; }
    }
    try {
      const r = await client.mutate(
        `mutation($id: Int!, $e: String!, $n: String) { cambiarEstadoCompromiso(id: $id, estado: $e, nota: $n) { success message } }`,
        { id: c.id, e: estado, n: nota });
      if (r.cambiarEstadoCompromiso.success) { success(r.cambiarEstadoCompromiso.message); cargar(); }
      else error(r.cambiarEstadoCompromiso.message);
    } catch { error('Error al cambiar el estado'); }
  };

  const vencidos = items.filter(c => c.semaforo === 'vencido').length;
  const proximos = items.filter(c => c.semaforo === 'proximo').length;

  return (
    <div className="mc-page">
      <div className="mc-header">
        <div>
          <h2>Mis compromisos</h2>
          <p>Lo que tienes pendiente en las novedades de todos los proyectos.</p>
        </div>
        <label className="mc-check">
          <input type="checkbox" checked={verCerrados} onChange={e => setVerCerrados(e.target.checked)} />
          Ver también los cerrados
        </label>
      </div>

      {!cargando && items.length > 0 && (vencidos > 0 || proximos > 0) && (
        <div className={`bit-aviso ${vencidos > 0 ? 'rojo' : 'ambar'}`} style={{ cursor: 'default' }}>
          <span className="bit-aviso-icono">{vencidos > 0 ? '⚠' : '⏰'}</span>
          <span className="bit-aviso-txt">
            <strong>
              {vencidos > 0
                ? `Tienes ${vencidos} compromiso${vencidos === 1 ? '' : 's'} vencido${vencidos === 1 ? '' : 's'}`
                : `Tienes ${proximos} compromiso${proximos === 1 ? '' : 's'} por vencer`}
            </strong>
            <small>{vencidos > 0 && proximos > 0 ? `y ${proximos} por vencer en los próximos días` : 'Revísalos antes de que se acumulen'}</small>
          </span>
        </div>
      )}

      {cargando ? (
        <div className="bit-cargando">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="bit-vacio">
          {verCerrados ? 'No tienes compromisos registrados.' : 'No tienes compromisos abiertos. Todo al día.'}
        </div>
      ) : (
        <div className="bit-lista">
          {items.map(c => (
            <div key={c.id} className={`bit-item sem-${c.semaforo}`}>
              <div className="bit-item-top" style={{ cursor: 'default' }}>
                <div className="bit-item-info">
                  <div className="bit-item-desc">{c.descripcion}</div>
                  <div className="bit-item-meta">
                    <button className="mc-proyecto" onClick={() => onAbrirProyecto && onAbrirProyecto(c.proyecto_id)}>
                      {c.proyecto_nombre}{c.apartamento ? ` · ${c.apartamento}` : ''}
                    </button>
                    <span className="bit-sep">·</span>
                    <span>{fmtFecha(c.fecha_entrega)}</span>
                    {c.estado !== 'cumplido' && c.estado !== 'cancelado' && (
                      <>
                        <span className="bit-sep">·</span>
                        <span className={`bit-dias ${c.semaforo}`}>{textoDias(c.dias_para_vencer)}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="bit-item-acciones">
                  <span className={`bit-estado ${c.estado}`}>{c.estado.replace('_', ' ')}</span>
                  {c.estado !== 'cumplido' && c.estado !== 'cancelado' && (
                    <>
                      {c.estado === 'pendiente' && (
                        <button className="bit-btn bit-btn-sm" onClick={() => cambiarEstado(c, 'en_proceso')}>En proceso</button>
                      )}
                      <button className="bit-btn bit-btn-sm bit-ok" onClick={() => cambiarEstado(c, 'cumplido')}>✓ Cumplido</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MisCompromisos;
